import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material';
import { useApiQuery } from '@/hooks/useApi';
import { useProfile } from '@/hooks/useProfile';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { queryTableNameUtil } from '@/util/queryTableNameUtil';

type Props = {
  onSuggestionClick: (message?: string, suggestion?: any) => void;
  inputValue: string;
};

const DEBOUNCE_TIME = 400;
const MAX_SUGGESTIONS = 3;
const MIN_QUERY_LENGTH = 3;

const AutocompleteSuggestions: React.FC<Props> = ({
  onSuggestionClick,
  inputValue,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [debouncedValue, setDebouncedValue] = useState(
    inputValue.trim().toLowerCase(),
  );
  const { profile } = useProfile();
  const theme = useTheme();
  const expSystemKey = profile?.value?.[0]?.eXpSystemKey!;
  const userUuid = profile?.value?.[0]?.eXpMember?.Uuid;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue.trim().toLowerCase());
      setSelectedIndex(-1);
    }, DEBOUNCE_TIME);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const {
    data: suggestionsData,
    isLoading,
    isError,
  } = useApiQuery<any>(
    ['/prompts/search', userUuid as string, debouncedValue],
    '/user/prompts/search',
    {
      refetchOnWindowFocus: false,
      enabled:
        !!debouncedValue &&
        debouncedValue.trim().length >= MIN_QUERY_LENGTH &&
        !!userUuid,
      staleTime: 10 * 60 * 1000,
      placeholderData: debouncedValue ? keepPreviousData : undefined,
      retry: 1,
    },
    false,
    {
      query: debouncedValue,
    },
  );

  const suggestions = useMemo(() => {
    if (!suggestionsData?.data || !Array.isArray(suggestionsData.data))
      return [];
    return suggestionsData.data.slice(0, MAX_SUGGESTIONS);
  }, [suggestionsData]);

  const suggestionHandler = useCallback(
    (suggestion: any) => {
      setDebouncedValue('');
      setSelectedIndex(-1);

      const { sqlQuery, tableName } = queryTableNameUtil(
        suggestion.sqlQuery,
        suggestion.tableName,
        expSystemKey,
      );

      onSuggestionClick(suggestion.question, {
        ...suggestion,
        sqlQuery,
        tableName,
      });
    },
    [onSuggestionClick, expSystemKey],
  );

  const stateRef = useRef({
    suggestions: [] as any[],
    selectedIndex: -1,
    suggestionHandler,
  });

  useEffect(() => {
    stateRef.current = {
      suggestions,
      selectedIndex,
      suggestionHandler,
    };
  }, [suggestions, selectedIndex, suggestionHandler]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const { suggestions, selectedIndex, suggestionHandler } =
        stateRef.current;

      if (suggestions.length === 0) return;

      const activeEl = document.activeElement;
      const isInputFocused =
        activeEl?.tagName === 'INPUT' ||
        activeEl?.tagName === 'TEXTAREA' ||
        activeEl?.getAttribute('contenteditable') === 'true';

      if (!isInputFocused) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        const len = suggestions.length;
        setSelectedIndex((prev) => (prev + 1) % len);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        const len = suggestions.length;
        setSelectedIndex((prev) => (prev - 1 + len) % len);
      } else if (e.key === 'Enter') {
        if (selectedIndex === -1) return;
        const selectedSuggestion = suggestions[selectedIndex];
        if (selectedSuggestion) {
          e.preventDefault();
          e.stopPropagation();
          suggestionHandler(selectedSuggestion);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(-1);
        setDebouncedValue('');
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  const highlightMatch = useCallback(
    (text: string, query: string) => {
      if (!query || !query.trim()) return text;

      const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);

      if (words.length === 0) return text;

      const escapedWords = words.map((word) =>
        word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      );
      const pattern = new RegExp(`(${escapedWords.join('|')})`, 'i');
      const parts = text.split(pattern);

      return (
        <>
          {parts.map((part, i) =>
            pattern.test(part) ? (
              <Box
                key={i}
                component="span"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.primary.main,
                }}
              >
                {part}
              </Box>
            ) : (
              part
            ),
          )}
        </>
      );
    },
    [theme.palette.primary.main],
  );

  if (suggestions.length === 0 || isLoading || isError) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      role="listbox"
      aria-label="Search suggestions"
      data-testid="autocomplete-suggestions"
      sx={{
        width: '120%',
        maxHeight: '200px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.palette.background.default,
          borderRadius: '12px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.grey[500],
          borderRadius: '3px',
        },
        [theme.breakpoints.down('sm')]: {
          maxWidth: '355px',
        },
      }}
    >
      {suggestions.map((suggestion: any, index: number) => {
        const displayQuestion = suggestion.question || '';
        return (
          <Box
            key={`${suggestion.question}-${index}`}
            role="option"
            aria-selected={index === selectedIndex}
            data-testid={`autocomplete-suggestion-${index}`}
            onClick={() => suggestionHandler(suggestion)}
            sx={{
              cursor: 'pointer',
              backgroundColor:
                index === selectedIndex
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
              transition: 'background-color 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              '&:first-of-type': {
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
              },
              '&:last-of-type': {
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
              },
            }}
          >
            <Box
              sx={{
                borderLeft:
                  selectedIndex === index
                    ? `4px solid ${theme.palette.text.primary}`
                    : 'none',
                padding: '12px 16px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  textAlign: 'left',
                }}
                noWrap
              >
                {highlightMatch(displayQuestion, inputValue)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
};

export default AutocompleteSuggestions;
